import { z } from 'zod';
import type { GeolocationConsent } from '@/types/auth.types';
import type { translations } from '@/translations/translations';
import { COUNTRIES, COUNTRY_MAP_BY_ISO, type CountryData } from '@/lib/countries';

/** Backend spec 004 — collection place and geolocation consent. */

/** Must match GEOLOCATION_CONSENT_VERSION on the backend; bump with the text. */
export const GEOLOCATION_CONSENT_VERSION = 1;
export const PLACE_QUERY_MIN = 2;
export const PLACE_QUERY_MAX = 80;

export const placeSchema = z
  .object({
    placeId: z.string().min(1).max(64),
    city: z.string().min(1).max(120),
    region: z.string().max(120).nullable(),
    countryCode: z.string().regex(/^[A-Z]{2}$/),
    countryName: z.string().min(1).max(80),
  })
  .strict();

export const placesResponseSchema = z.object({ items: z.array(placeSchema).max(8) }).strict();
export const reverseResponseSchema = z.object({ place: placeSchema }).strict();

/**
 * The ISO code of a place, from the code or from the name.
 *
 * The server publishes a real code, but the screen has to read the places
 * of a server that has not been updated yet, where the country could be a
 * name or a lower case code.
 */
export function resolveCountryCode(code: string, name: string): string {
  const trimmed = code.trim();
  if (/^[A-Za-z]{2}$/.test(trimmed)) return trimmed.toUpperCase();
  const byCode = COUNTRY_MAP_BY_ISO[trimmed.toUpperCase()];
  if (byCode) return byCode.iso;
  const byName = COUNTRIES.find(
    (country) => country.name.trim().toLowerCase() === name.trim().toLowerCase(),
  );
  if (byName) return byName.iso;
  return 'XX';
}

/** The name of a place, from its ISO code, in the language of the client. */
export function resolveCountryName(code: string, name: string): string {
  const known = COUNTRY_MAP_BY_ISO[code]?.name;
  if (known) return known;
  const trimmed = name.trim();
  return trimmed || code;
}

/**
 * One collection place of a collector, as the admin screens read it.
 *
 * A place with no city, or with a country that could not be resolved, is
 * still a place: it is read, and the screen shows a placeholder for what is
 * missing.
 */
export const collectorPlaceSchema = z
  .object({
    placeId: z.string().min(1).max(64),
    city: z.string().max(120),
    region: z.string().max(120).nullable(),
    countryCode: z.string().max(80),
    countryName: z.string().max(80),
    value: z.number().int().nonnegative(),
    lastCollectedAt: z.string().datetime({ offset: true }).nullable(),
  })
  .strict()
  .transform((place) => {
    const countryCode = resolveCountryCode(place.countryCode, place.countryName);
    return {
      ...place,
      city: place.city.trim(),
      countryCode,
      countryName: resolveCountryName(countryCode, place.countryName),
    };
  });

/**
 * The collection places of a collector.
 *
 * The server answers the standard envelope. The bare shape is accepted too,
 * so the screen keeps working while the two sides are not deployed at the
 * same time.
 */
export const collectorPlacesResponseSchema = z
  .union([
    z
      .object({
        status: z.literal(200),
        error: z.literal(false),
        message: z.literal('COLLECTOR_PLACES_LISTED'),
        data: z.object({ items: z.array(z.unknown()) }).strict(),
        meta: z.object({
          request_id: z.string().min(1),
          served_at: z.string().datetime({ offset: true }),
        }).strict(),
      })
      .strict(),
    z.object({ items: z.array(z.unknown()) }).strict(),
  ])
  .transform((payload) =>
    'data' in payload ? payload.data.items : payload.items,
  );
/**
 * The places that can be displayed, and the ones that could not be.
 *
 * A row is read on its own: a place the client cannot read is left out and
 * counted, and the other places are still displayed. Reading the whole array
 * at once would lose every place because of one malformed row.
 */
export function readCollectorPlaces(items: unknown[]): {
  places: z.infer<typeof collectorPlaceSchema>[];
  skipped: number;
} {
  const places: z.infer<typeof collectorPlaceSchema>[] = [];
  let skipped = 0;
  for (const item of items) {
    const parsed = collectorPlaceSchema.safeParse(item);
    if (parsed.success) {
      places.push(parsed.data);
    } else {
      skipped += 1;
    }
  }
  return { places, skipped };
}
export const declaredPlaceHistoryResponseSchema = z
  .object({
    status: z.literal(200),
    error: z.literal(false),
    message: z.literal('DECLARED_PLACES_LISTED'),
    data: z.object({
      items: z.array(placeSchema.extend({ lastCollectedAt: z.string().datetime({ offset: true }) }).strict()).max(5),
    }).strict(),
    meta: z.object({ request_id: z.string().min(1), served_at: z.string().datetime({ offset: true }) }).strict(),
  })
  .strict();

export const consentResponseSchema = z
  .object({
    geolocationConsent: z.enum(['granted', 'denied']),
    geolocationConsentAt: z.string().datetime({ offset: true }),
    geolocationConsentVersion: z.number().int().positive(),
  })
  .strict();

export type Place = z.infer<typeof placeSchema>;
export type CollectorPlace = z.infer<typeof collectorPlaceSchema>;
export type DeclaredPlaceHistoryItem = z.infer<typeof declaredPlaceHistoryResponseSchema>['data']['items'][number];
export type PlaceSource = 'declared' | 'geolocated';
export type CollectionPlace = Place & { source: PlaceSource };
export type CollectionPlaceCopy = (typeof translations)['fr']['collectionPlace'];
export type CollectionPlaceErrorKey = 'consentRequired' | 'notFound' | 'rateLimited' | 'unavailable';

export const collectionPlaceQueryKey = (userId: string, language: 'fr' | 'en') =>
  ['geo', 'reverse', userId, language] as const;

export function createDeclaredPlace(cityInput: string, country: CountryData, language: 'fr' | 'en'): Place | undefined {
  const city = cityInput.trim();
  if (city.length === 0 || city.length > 120) return undefined;
  let hash = 2166136261;
  for (const character of city.toLocaleLowerCase()) {
    hash = Math.imul(hash ^ character.charCodeAt(0), 16777619);
  }
  const slug = city.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 30) || 'locality';
  const countryName = new Intl.DisplayNames([language], { type: 'region' }).of(country.iso) ?? country.name;
  return {
    placeId: `declared:${country.iso}:${slug}:${(hash >>> 0).toString(16).padStart(8, '0')}`,
    city,
    region: null,
    countryCode: country.iso,
    countryName,
  };
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const toConsent = (decision: unknown, version: unknown, at: unknown): GeolocationConsent | undefined =>
  (decision === 'granted' || decision === 'denied') && typeof version === 'number' && typeof at === 'string'
    ? { decision, version, at }
    : undefined;

/**
 * Reads the consent of a user record: the flat API fields (login, /auth/me) or
 * the nested shape this client stores, so a reload keeps it.
 */
export function toGeolocationConsent(record: Record<string, unknown>): GeolocationConsent | undefined {
  const nested = record.geolocationConsent;
  if (isRecord(nested)) return toConsent(nested.decision, nested.version, nested.at);
  return toConsent(nested, record.geolocationConsentVersion, record.geolocationConsentAt);
}

/** Asked again when never answered, or answered for an older text. */
export const needsConsentAnswer = (consent: GeolocationConsent | undefined) =>
  consent === undefined || consent.version !== GEOLOCATION_CONSENT_VERSION;

export const isConsentGranted = (consent: GeolocationConsent | undefined) =>
  consent?.decision === 'granted' && consent.version === GEOLOCATION_CONSENT_VERSION;

export const isPlaceQuery = (query: string) => {
  const length = query.trim().length;
  return length >= PLACE_QUERY_MIN && length <= PLACE_QUERY_MAX;
};

/** HTTP status → translation key, per the outcome tables of spec 004 §3. */
export function collectionPlaceErrorKey(httpStatus: number | undefined): CollectionPlaceErrorKey {
  if (httpStatus === 403) return 'consentRequired';
  if (httpStatus === 404) return 'notFound';
  if (httpStatus === 429) return 'rateLimited';
  return 'unavailable';
}

/** The multipart form value every upload carries (backend parses it strictly). */
export const collectionPlaceField = (place: CollectionPlace) => JSON.stringify(place);

/** Backend spec 004 §3.4: the server refuses a write of a session without its place. */
export const PLACE_REQUIRED_HEADERS = { 'X-Collection-Place': 'required' } as const;
